import React from 'react';
import { X, ListChecks } from 'lucide-react';
import { cn } from '@/lib/utils';
import { tone, LevelIcon, DoctorFix, DismissFinding, SettingChips, findingTone } from './DoctorParts';

/*
 * r16sd (design 5.6): what a save found - the settings that cannot work together with what was just saved, each with its fixes.
 * The save itself always went through (a WARN never blocks; a BLOCK refuses only its own setting, which the blocked[] toast names).
 * Shown at the top of the slot settings page until closed or the next save. Stage 5 (review SF1): a fix of kind "set" is one click
 * (POST .../doctor/fix; with unsaved edits on the page it opens the setting instead), "focus" opens the setting, a hint is text, and
 * a WARN / INFO finding carries "Don't show again". A finding fixed or hidden here leaves the card (onResolved).
 */
export default function SaveFindingsCard({ findings, slotName, deviceId, onFocusSetting, onClose, cardRef, applyBlocked, onResolved }) {
  const list = Array.isArray(findings) ? findings : [];
  if (!list.length) return null;
  const top = tone(findingTone(list[0].level));
  return (
    <div ref={cardRef} className={cn('rounded-2xl border p-4 sm:p-5', top.box)} role="status" data-testid="save-findings">
      <div className="mb-3 flex items-center gap-2.5">
        <span className={cn('inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ring-1', top.chip)}>
          <ListChecks className="h-4 w-4" aria-hidden />
        </span>
        <h3 className="min-w-0 flex-1 text-sm font-semibold text-foreground">
          Saved. {list.length === 1 ? 'One thing to check' : `${list.length} things to check`} with these settings
        </h3>
        {onClose && (
          <button type="button" onClick={onClose} className="tap rounded-full p-1.5 text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
            aria-label="Close" data-testid="save-findings-close">
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
      <ul className="space-y-2.5">
        {list.map((f, i) => (
          <li key={`${f.rule}-${i}`} className="flex gap-3 rounded-xl bg-background/40 px-3 py-2.5 ring-1 ring-border/60"
            data-testid={`save-finding-${f.rule}`} data-level={f.level}>
            <LevelIcon level={findingTone(f.level)} className="mt-0.5" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <p className="text-sm leading-relaxed text-foreground text-pretty">{f.text}</p>
              <SettingChips settings={f.settings} />
              {(f.fixes.length > 0 || f.level !== 'BLOCK') && (
                <div className="flex flex-wrap items-center gap-2 pt-0.5">
                  {f.fixes.map((x) => (
                    <DoctorFix key={x.id} fix={x} settings={f.settings} slotName={slotName} deviceId={deviceId} onFocusSetting={onFocusSetting}
                      testid={`save-finding-${f.rule}-fix-${x.id}`} rule={f.rule} nearText={f.text} applyBlocked={applyBlocked}
                      onDone={onResolved ? () => onResolved(f.rule, 'fixed') : undefined} />
                  ))}
                  {f.level !== 'BLOCK' && (
                    <DismissFinding rule={f.rule} slotName={slotName} testid={`save-finding-${f.rule}-dismiss`}
                      onDone={onResolved ? () => onResolved(f.rule, 'hidden') : undefined} />
                  )}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
